import { useEffect, useState } from "react";
import { Status } from "../design/Hinweis";
import { Symbol } from "../design/Symbol";
import { jagdtagBegleiten, offlineAbmelden, useOfflinestand } from "./offline";
import { useServerkontakt } from "./serverzeit";
import { useSitzung } from "./sitzung";

/**
 * Oberfläche der Offline-Daten des Jagdtags (oberflaeche.md §4.1, §7, §9, §10; Anforderungen §7a K60, N-42).
 * `OfflineBegleitung` steht einmal in der App; die Hinweise binden die Ansichten ein, die sie laut Vertrag zeigen.
 */
export function OfflineBegleitung() {
  const { status, uid } = useSitzung();
  useEffect(() => {
    if (status === "ohne") void offlineAbmelden();
  }, [status]);
  return status === "angemeldet" && uid ? <Begleitung uid={uid} /> : null;
}

function Begleitung({ uid }: { uid: string }) {
  useServerkontakt();
  useEffect(() => jagdtagBegleiten(uid), [uid]);
  return null;
}

/** Hat das Gerät Netz? (`online`/`offline` des Browsers) */
export function useNetz(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const an = () => setOnline(true);
    const aus = () => setOnline(false);
    window.addEventListener("online", an);
    window.addEventListener("offline", aus);
    setOnline(navigator.onLine);
    return () => { window.removeEventListener("online", an); window.removeEventListener("offline", aus); };
  }, []);
  return online;
}

/** `status` „Offline bereit“ (§9, §10): im `article` der Drückjagd in Meine Drückjagden und in der Kontextzeile ihrer Startansicht. */
export function OfflineBereit({ drueckjagdId, className = "eintrag__status" }: { drueckjagdId: string; className?: string }) {
  const { bereit } = useOfflinestand();
  if (!bereit.has(drueckjagdId)) return null;
  return <span className={className} role="status"><Symbol name="offline-bereit" /><span>Offline bereit</span></span>;
}

export const WORTLAUT_DATEN_FEHLEN = "Die Daten für den Jagdtag fehlen. Bitte mit Netz öffnen.";
export const WORTLAUT_EINWEISUNG_VOR_DEM_JAGDTAG = "Die Einweisung ist vor dem Jagdtag nur mit Netz lesbar.";

/**
 * Hinweise ohne Netz (§9): am Jagdtag fehlen die Daten (Speicher vom Browser geleert); vor dem Jagdtag ist die
 * Einweisung nur mit Netz lesbar (LIM-0008).
 */
export function OfflineHinweis({ ansicht }: { ansicht?: string }) {
  const online = useNetz();
  const { fehlen, jagdtag } = useOfflinestand();
  if (online) return null;
  const text = fehlen ? WORTLAUT_DATEN_FEHLEN : !jagdtag && ansicht === "Einweisung" ? WORTLAUT_EINWEISUNG_VOR_DEM_JAGDTAG : null;
  if (!text) return null;
  return <Status symbol="offline">{text}</Status>;
}
